import React, { useState, useEffect, useRef } from 'react';
import {
    BEHAVIOR_SCHEMAS as IMPORTED_SCHEMAS,
    getAddableBehaviorTypes,
} from 'trace-viewer/behaviors';

// List of behavior types available for adding
const ADDABLE_BEHAVIOR_TYPES = getAddableBehaviorTypes();

// Get behavior-level description for tooltips
const getBehaviorDescription = (type: string): string | undefined => {
    return IMPORTED_SCHEMAS[type]?.description;
};

const inputStyle: React.CSSProperties = {
    padding: '4px 8px',
    backgroundColor: '#333',
    border: '1px solid #555',
    borderRadius: '3px',
    color: '#fff',
    fontSize: '12px',
    outline: 'none',
};

const NEW_SCRIPT_ENTRY = '+ New Script...';

interface AddBehaviorButtonProps {
    onAdd: (type: string) => void;
    existingTypes: string[];
    /** Script types from ScriptRegistry (shown alongside built-in behaviors) */
    scriptTypes?: string[];
    /** Called when "New Script..." is selected */
    onNewScript?: () => void;
}

export const AddBehaviorButton: React.FC<AddBehaviorButtonProps> = ({ onAdd, existingTypes, scriptTypes, onNewScript }) => {
    const [showPopover, setShowPopover] = useState(false);
    const [search, setSearch] = useState('');
    const [highlightIndex, setHighlightIndex] = useState(0);
    const [popoverRect, setPopoverRect] = useState({ top: 0, left: 0, width: 0, height: 280 });
    const buttonRef = useRef<HTMLButtonElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    // Filter out types that already exist (behaviors are singleton)
    const builtInTypes = ADDABLE_BEHAVIOR_TYPES.filter(type => !existingTypes.includes(type));
    const availableScriptTypes = (scriptTypes ?? []).filter(type => !existingTypes.includes(type));
    const availableTypes = [...builtInTypes, ...availableScriptTypes];

    const searchLower = search.toLowerCase();
    const filteredBuiltIn = builtInTypes.filter(type => type.toLowerCase().includes(searchLower));
    const filteredScripts = availableScriptTypes.filter(type => type.toLowerCase().includes(searchLower));

    // "New Script..." always appears first (if it matches search or search is empty)
    const showNewScript = onNewScript && (!search || NEW_SCRIPT_ENTRY.toLowerCase().includes(searchLower));

    // Combined list: [New Script..., ...built-in, ...scripts]
    const filteredTypes = [
        ...(showNewScript ? [NEW_SCRIPT_ENTRY] : []),
        ...filteredBuiltIn,
        ...filteredScripts,
    ];

    // Compute popover rect from button position
    const computePopoverRect = () => {
        if (!buttonRef.current) return;
        const rect = buttonRef.current.getBoundingClientRect();
        const inset = rect.width * 0.1;
        const desiredHeight = 280;
        const margin = 8;
        const spaceBelow = window.innerHeight - rect.bottom - margin;
        const spaceAbove = rect.top - margin;

        let top: number;
        let height: number;
        if (spaceBelow >= desiredHeight) {
            top = rect.bottom + 4;
            height = desiredHeight;
        } else if (spaceAbove > spaceBelow) {
            height = Math.min(desiredHeight, spaceAbove);
            top = rect.top - 4 - height;
        } else {
            top = rect.bottom + 4;
            height = Math.max(120, spaceBelow);
        }

        setPopoverRect({ top, left: rect.left + inset, width: rect.width - inset * 2, height });
    };

    // Toggle popover — compute rect synchronously before first render
    const togglePopover = () => {
        if (!showPopover) {
            computePopoverRect();
            setShowPopover(true);
        } else {
            setShowPopover(false);
        }
    };

    // Focus search input when popover opens; reset state on close
    useEffect(() => {
        if (showPopover) {
            requestAnimationFrame(() => searchInputRef.current?.focus());
        } else {
            setSearch('');
            setHighlightIndex(0);
        }
    }, [showPopover]);

    // Clamp highlight index when filtered list changes
    useEffect(() => {
        setHighlightIndex(prev => Math.min(prev, Math.max(0, filteredTypes.length - 1)));
    }, [filteredTypes.length]);

    // Click outside to close
    useEffect(() => {
        if (!showPopover) return;
        const handleClick = (e: MouseEvent) => {
            if (
                popoverRef.current && !popoverRef.current.contains(e.target as Node) &&
                buttonRef.current && !buttonRef.current.contains(e.target as Node)
            ) {
                setShowPopover(false);
            }
        };
        // Delay attaching so the opening click doesn't immediately close
        const id = requestAnimationFrame(() => document.addEventListener('mousedown', handleClick));
        return () => {
            cancelAnimationFrame(id);
            document.removeEventListener('mousedown', handleClick);
        };
    }, [showPopover]);

    const handleSelect = (type: string) => {
        if (type === NEW_SCRIPT_ENTRY) {
            onNewScript?.();
        } else {
            onAdd(type);
        }
        setShowPopover(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlightIndex(prev => Math.min(prev + 1, filteredTypes.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlightIndex(prev => Math.max(prev - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (filteredTypes[highlightIndex]) {
                handleSelect(filteredTypes[highlightIndex]);
            }
        } else if (e.key === 'Escape') {
            setShowPopover(false);
        }
    };

    return (
        <div style={{ position: 'relative' }}>
            <button
                ref={buttonRef}
                data-action="add-behavior"
                onClick={togglePopover}
                style={{
                    width: '100%',
                    padding: '10px 16px',
                    backgroundColor: '#383838',
                    border: '1px dashed #555',
                    borderRadius: '4px',
                    color: '#aaa',
                    cursor: 'pointer',
                    fontSize: '13px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                }}
            >
                <span style={{ fontSize: '16px' }}>+</span>
                Add Behavior
            </button>

            {showPopover && (
                <div
                    ref={popoverRef}
                    style={{
                        position: 'fixed',
                        top: popoverRect.top,
                        left: popoverRect.left,
                        width: popoverRect.width,
                        height: popoverRect.height,
                        backgroundColor: '#2d2d2d',
                        border: '1px solid #444',
                        borderRadius: '4px',
                        overflow: 'hidden',
                        zIndex: 1000,
                        boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    {/* Search input */}
                    <div style={{ padding: '8px', borderBottom: '1px solid #3a3a3a' }}>
                        <input
                            ref={searchInputRef}
                            type="text"
                            placeholder="Search behaviors..."
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setHighlightIndex(0); }}
                            onKeyDown={handleKeyDown}
                            style={{
                                ...inputStyle,
                                width: '100%',
                                boxSizing: 'border-box',
                            }}
                        />
                    </div>
                    {/* Scrollable list */}
                    <div style={{ overflow: 'auto', flex: 1 }}>
                        {filteredTypes.length > 0 ? (
                            filteredTypes.map((type, i) => {
                                const isNewScript = type === NEW_SCRIPT_ENTRY;
                                const isScript = !isNewScript && availableScriptTypes.includes(type);
                                return (
                                    <button
                                        key={type}
                                        onClick={() => handleSelect(type)}
                                        title={isNewScript ? 'Create a new script behavior' : getBehaviorDescription(type)}
                                        style={{
                                            width: '100%',
                                            padding: '8px 12px',
                                            backgroundColor: i === highlightIndex ? '#383838' : 'transparent',
                                            border: 'none',
                                            borderBottom: isNewScript ? '1px solid #3a3a3a' : 'none',
                                            color: isNewScript ? '#7ab' : isScript ? '#9cb' : '#fff',
                                            cursor: 'pointer',
                                            fontSize: '12px',
                                            textAlign: 'left',
                                            fontStyle: isNewScript ? 'italic' : 'normal',
                                        }}
                                        onMouseEnter={() => setHighlightIndex(i)}
                                    >
                                        {type}
                                    </button>
                                );
                            })
                        ) : (
                            <div style={{ padding: '8px 12px', color: '#666', fontSize: '12px' }}>
                                {availableTypes.length === 0 ? 'All behaviors added' : 'No matches'}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
